import React from "react";
import { cn } from "@/lib/utils";
import type { IndexationKPIs, DuplicateUnit, KpiFilter } from "../hooks/useIndexationData";
import { DuplicateUnitsPopover } from "./DuplicateUnitsPopover";

interface IndexationKPIBarProps {
  kpis: IndexationKPIs;
  duplicateUnits: DuplicateUnit[];
  isLoading: boolean;
  activeKpiFilter: KpiFilter | null;
  onKpiFilterToggle: (filter: KpiFilter) => void;
}

/**
 * Seven filter-aware KPI tiles. Tile order (product spec):
 *  1. Units
 *  2. Units met meerdere contracten   ← popover
 *  3. Contracten actief               ← subline: X lopend / Y in opzeg
 *  4. Reeds geïndexeerd dit jaar
 *  5. Nog te indexeren dit jaar
 *  6. Te indexeren deze maand         ← clickable sub-filter, subset of #5
 *  7. Te indexeren volgende maand     ← clickable sub-filter, subset of #5
 *
 * KPIs refresh with search/building filters but NOT with the tab switch.
 * "—" placeholders during the first load (all zeros + loading).
 */
export const IndexationKPIBar: React.FC<IndexationKPIBarProps> = ({
  kpis,
  duplicateUnits,
  isLoading,
  activeKpiFilter,
  onKpiFilterToggle,
}) => {
  const showPlaceholder =
    isLoading &&
    kpis.unitsCount === 0 &&
    kpis.contractsActiveTotal === 0;

  const fmt = (n: number): string =>
    showPlaceholder ? "—" : n.toLocaleString("nl-BE");

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-7">
      {/* 1. Units */}
      <KPITile value={fmt(kpis.unitsCount)} label="Units" isLoading={showPlaceholder} />

      {/* 2. Units met meerdere contracten — popover */}
      <DuplicateUnitsPopover
        count={showPlaceholder ? 0 : kpis.unitsWithMultipleActiveContracts}
        duplicateUnits={duplicateUnits}
        displayValue={fmt(kpis.unitsWithMultipleActiveContracts)}
        isLoading={showPlaceholder}
      />

      {/* 3. Contracten actief */}
      <KPITile
        value={fmt(kpis.contractsActiveTotal)}
        label="Contracten actief"
        isLoading={showPlaceholder}
        subline={
          showPlaceholder
            ? undefined
            : `${kpis.contractsLopend.toLocaleString("nl-BE")} lopend / ${kpis.contractsInOpzeg.toLocaleString("nl-BE")} in opzeg`
        }
      />

      {/* 4. Reeds geïndexeerd dit jaar */}
      <KPITile
        value={fmt(kpis.indexedThisYear)}
        label="Reeds geïndexeerd dit jaar"
        isLoading={showPlaceholder}
        accentClass="text-moss-primary"
      />

      {/* 5. Nog te indexeren dit jaar */}
      <KPITile
        value={fmt(kpis.notIndexedThisYear)}
        label="Nog te indexeren dit jaar"
        isLoading={showPlaceholder}
        accentClass="text-brick-primary"
      />

      {/* 6. Te indexeren deze maand — clickable sub-filter, subset of #5 */}
      <KPITile
        value={fmt(kpis.toIndexThisMonth)}
        label="Te indexeren deze maand"
        isLoading={showPlaceholder}
        accentClass="text-brick-primary"
        isSubset
        isActive={activeKpiFilter === "thisMonth"}
        onClick={
          !showPlaceholder && kpis.toIndexThisMonth > 0
            ? () => onKpiFilterToggle("thisMonth")
            : undefined
        }
      />

      {/* 7. Te indexeren volgende maand — clickable sub-filter, subset of #5 */}
      <KPITile
        value={fmt(kpis.toIndexNextMonth)}
        label="Te indexeren volgende maand"
        isLoading={showPlaceholder}
        accentClass="text-brick-primary"
        isSubset
        isActive={activeKpiFilter === "nextMonth"}
        onClick={
          !showPlaceholder && kpis.toIndexNextMonth > 0
            ? () => onKpiFilterToggle("nextMonth")
            : undefined
        }
      />
    </div>
  );
};

// ─── Internal plain tile ──────────────────────────────────────────────────────

interface KPITileProps {
  value: string;
  label: string;
  subline?: string;
  isLoading?: boolean;
  accentClass?: string;
  /** Tiles 6 & 7 are subsets of tile 5 */
  isSubset?: boolean;
  /** Whether this tile is the active sub-filter */
  isActive?: boolean;
  /** When provided the tile becomes interactive */
  onClick?: () => void;
}

const KPITile: React.FC<KPITileProps> = ({
  value,
  label,
  subline,
  isLoading = false,
  accentClass,
  isSubset = false,
  isActive = false,
  onClick,
}) => {
  const isClickable = !!onClick;

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => { if (e.key === "Enter" || e.key === " ") onClick?.(); }
          : undefined
      }
      className={cn(
        "rounded-lg border bg-card px-4 py-3 flex flex-col gap-1 transition-colors",
        isSubset && !isActive && "border-l-2 border-l-brick-secondary-fine",
        isActive && "border-l-2 border-l-moss-primary bg-moss-secondary-light",
        isClickable && !isActive && "cursor-pointer hover:bg-muted/50",
        isClickable && isActive && "cursor-pointer",
      )}
    >
      {/* Big number */}
      <span
        className={cn(
          "text-2xl font-bold leading-none",
          isLoading
            ? "text-neutral-secondary-mid"
            : isActive
              ? "text-moss-primary"
              : (accentClass ?? "text-neutral-primary"),
        )}
      >
        {value}
      </span>

      {/* Label */}
      <span
        className={cn(
          "text-xs font-medium uppercase tracking-wide",
          isActive ? "text-moss-primary" : "text-neutral-secondary-mid",
        )}
      >
        {label}
      </span>

      {/* Active badge */}
      {isActive && (
        <span className="text-xs text-moss-primary font-semibold mt-0.5">
          Actief ×
        </span>
      )}

      {/* Optional subline (tile 3 only) */}
      {subline && (
        <span className="text-xs text-neutral-secondary-mid mt-0.5">{subline}</span>
      )}
    </div>
  );
};
